import { BusinessInfo } from '@wix/bookings-uou-types';
import { getDayFromDateAsISO } from '../../mappers/date-mappers-helpers';
import type { Service } from '@wix/ambassador-bookings-services-v2-service/types';
import type { Event } from '@wix/ambassador-calendar-v3-event/types';
import {
  formatDuration,
  MINUTES_IN_DAY,
} from '@wix/bookings-catalog-calendar-viewer-utils/duration';

export interface SchedulingSectionViewModel {
  schedulingDaysViewModel?: SchedulingDayViewModel[];
  status: SchedulingSectionStatus;
  firstSessionDate?: string;
  lastSessionDate?: string;
  isBookable: boolean;
}

export interface SchedulingDayViewModel {
  date: string;
  dailySessions: DailySession[];
}

export interface DailySession {
  startTime: string;
  durationText: string;
  durationAriaLabel: string;
  staffName: string;
  locationName?: string;
  allDay?: boolean;
}

export interface FormattedDailySession extends DailySession {
  startDate: string;
  year?: string;
  date: string;
}

// https://stackoverflow.com/questions/63961803/eslint-says-all-enums-in-typescript-app-are-already-declared-in-the-upper-scope
// eslint-disable-next-line no-shadow
export enum SchedulingSectionStatus {
  LOADING = 'LOADING',
  EMPTY = 'EMPTY',
  FAILED = 'FAILED',
  SUCCESS = 'SUCCESS',
}

export const schedulingSectionViewModelFactory = ({
  events,
  businessInfo,
  firstSessionStart,
  lastSessionEnd,
  viewTimezone,
  isCourse = false,
  isBookable,
  t,
  service,
  isAllDayMultiDayEnabled = false,
}: {
  events?: Event[];
  businessInfo: BusinessInfo;
  firstSessionStart?: string;
  lastSessionEnd?: string;
  viewTimezone?: string;
  isCourse?: boolean;
  isBookable: boolean;
  t;
  service?: Service;
  isAllDayMultiDayEnabled?: boolean;
}): SchedulingSectionViewModel => {
  if (!events || events.length === 0) {
    return { status: SchedulingSectionStatus.EMPTY, isBookable };
  }

  const dateTimeFormatter = new Intl.DateTimeFormat(
    businessInfo.dateRegionalSettingsLocale,
    {
      weekday: 'long',
      hour: 'numeric',
      minute: 'numeric',
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      timeZone: viewTimezone,
    },
  );
  const dateFormatter = new Intl.DateTimeFormat(
    businessInfo.dateRegionalSettingsLocale,
    {
      weekday: 'long',
      day: 'numeric',
      month: 'short',
      timeZone: viewTimezone,
    },
  );
  let firstSessionDate, lastSessionDate;
  if (isCourse && firstSessionStart && lastSessionEnd) {
    const sessionRangeFormat = new Intl.DateTimeFormat(
      businessInfo.dateRegionalSettingsLocale,
      {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        timeZone: viewTimezone,
      },
    );
    firstSessionDate = sessionRangeFormat.format(new Date(firstSessionStart));
    lastSessionDate = sessionRangeFormat.format(new Date(lastSessionEnd));
  }

  const diffMinutes = (dt2, dt1) => {
    const diff = (dt2.getTime() - dt1.getTime()) / 1000;
    return Math.abs(Math.round(diff / 60));
  };

  // events api does not support multilingual so we take from services api.
  const staffNames = new Map<string, string>();
  const locationNames = new Map<string, string>();

  service?.staffMemberDetails?.staffMembers?.forEach((staff) => {
    staffNames.set(staff.staffMemberId!, staff.name!);
  });

  service?.locations?.forEach((location) => {
    if (location.business?.name) {
      locationNames.set(location.id!, location.business?.name!);
    }
  });

  const getStaffNameFromEvent = (event: Event) => {
    const staffMemberId = event.resources?.[0]?.id;
    return (
      ((service && staffMemberId && staffNames.get(staffMemberId)) ||
        event.resources?.[0]?.name) ??
      ''
    );
  };

  const getLocationNameFromEvent = (event: Event) => {
    const locationId = event.location?.id;
    return (
      ((service && locationId && locationNames.get(locationId)) ||
        event.location?.name) ??
      ''
    );
  };

  const formattedSessions: FormattedDailySession[] = events.map((event) => {
    const isAllDay = isAllDayMultiDayEnabled && event.allDay === true;
    const eventStartDate = new Date(event.start?.utcDate!);
    const dateParts = dateTimeFormatter.formatToParts(eventStartDate);
    const date = dateFormatter.format(eventStartDate);
    const year = dateParts.find(({ type }) => type === 'year')?.value;

    let startTime: string;
    let durationText: string;
    let durationAriaLabel: string;
    const durationInMinutes = diffMinutes(
      event.end?.utcDate,
      event.start?.utcDate,
    );
    const hour = dateParts.find(({ type }) => type === 'hour')?.value;
    const minute = dateParts.find(({ type }) => type === 'minute')?.value;
    const dayPeriod = dateParts.find(
      ({ type }) => type.toLocaleLowerCase() === 'dayperiod',
    )?.value;
    const formattedStartTime = `${hour}:${minute} ${dayPeriod ?? ''}`.trim();

    if (isAllDay) {
      startTime = formattedStartTime;
      const days = Math.max(1, Math.round(durationInMinutes / MINUTES_IN_DAY));
      const normalizedMinutes = days * MINUTES_IN_DAY;
      durationText = formatDuration(normalizedMinutes, t, false, true);
      durationAriaLabel = formatDuration(normalizedMinutes, t, true, true);
    } else {
      startTime = formattedStartTime;
      durationText = formatDuration(
        durationInMinutes,
        t,
        false,
        isAllDayMultiDayEnabled,
      );
      durationAriaLabel = formatDuration(
        durationInMinutes,
        t,
        true,
        isAllDayMultiDayEnabled,
      );
    }

    return {
      startDate: event.start!.utcDate!.toISOString(),
      date,
      year,
      staffName: getStaffNameFromEvent(event),
      startTime,
      durationText,
      durationAriaLabel,
      locationName: getLocationNameFromEvent(event),
      ...(isAllDay && { allDay: true }),
    };
  });

  return {
    schedulingDaysViewModel: formatDays(formattedSessions),
    status: SchedulingSectionStatus.SUCCESS,
    firstSessionDate,
    lastSessionDate,
    isBookable,
  };
};

function formatDays(
  formattedSessions: FormattedDailySession[],
): SchedulingDayViewModel[] {
  formattedSessions.sort((a, b) => {
    return (
      a.startDate.localeCompare(b.startDate) ||
      (a.locationName || '').localeCompare(b.locationName || '') ||
      a.staffName.localeCompare(b.staffName)
    );
  });
  const mappedDays: Record<string, SchedulingDayViewModel> = {};
  formattedSessions.forEach((formattedEvent) => {
    const dayKey = `${formattedEvent.date}-${formattedEvent.year}`;
    mappedDays[dayKey] = mappedDays[dayKey] || {
      date: formattedEvent.date,
      dailySessions: [],
    };
    mappedDays[dayKey].dailySessions.push({
      startTime: formattedEvent.startTime,
      staffName: formattedEvent.staffName,
      durationText: formattedEvent.durationText,
      durationAriaLabel: formattedEvent.durationAriaLabel,
      locationName: formattedEvent.locationName,
      ...(formattedEvent.allDay && { allDay: true }),
    });
  });

  return Object.values(mappedDays);
}

export const dummySchedulingViewModel = ({
  t,
  scheduleDays,
  businessInfo,
}: {
  t;
  scheduleDays: number;
  businessInfo: BusinessInfo;
}): SchedulingSectionViewModel => {
  const now = new Date(new Date().setUTCHours(10, 0, 0, 0));
  const staffName = t('dummy-data.staff-name');
  const locationName = t('dummy-data.location-name');
  const durationInMinutes = 45;
  const endDate = new Date(now.getTime() + durationInMinutes * 60 * 1000);
  const placeholderEvent: Event = {
    start: { utcDate: now },
    end: { utcDate: endDate },
    resources: [{ name: staffName }],
    location: { name: locationName },
  };
  const events: Event[] = Array.from({ length: scheduleDays }, (_, index) => ({
    ...placeholderEvent,
    start: { utcDate: new Date(getDayFromDateAsISO(now, index)) },
    end: {
      utcDate: new Date(
        new Date(getDayFromDateAsISO(now, index)).getTime() +
          durationInMinutes * 60 * 1000,
      ),
    },
  }));
  return schedulingSectionViewModelFactory({
    events,
    businessInfo,
    t,
    isBookable: true,
  });
};
